import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { IMAGES } from "@/lib/images";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToFleet = () => document.querySelector("#fleet")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" ref={ref} className="relative h-screen min-h-[680px] overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="Luxury arrival" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E]/70 via-[#0B0C0E]/35 to-[#0B0C0E]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C0E]/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-24 lg:pb-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }} className="flex items-center gap-4 mb-8">
          <span className="gold-rule" />
          <span className="eyebrow">{t("hero.tagline")}</span>
        </motion.div>

        <h1 className="font-serif-elt font-light text-[var(--elt-ivory)] leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-8" data-testid="hero-headline">
          <MaskedLine delay={0.35}>{t("hero.line1")}</MaskedLine>
          <MaskedLine delay={0.55} className="text-[var(--elt-gold-light)] italic">{t("hero.line2")}</MaskedLine>
        </h1>

        <MaskedLine delay={0.8}>
          <p className="text-base md:text-lg font-light text-[var(--elt-ivory)]/85 tracking-wide">{t("hero.sub")}</p>
        </MaskedLine>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-6 max-w-xl text-sm font-light leading-relaxed text-[var(--elt-muted)]">
          {t("hero.desc")}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/request" data-testid="hero-request-journey-btn" className="btn-gold justify-center">{t("cta.request")}</Link>
          <button onClick={scrollToFleet} data-testid="hero-discover-fleet-btn" className="btn-ghost justify-center">{t("cta.discoverFleet")}</button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 1 }}
          className="mt-12 flex items-center gap-3 text-[0.65rem] tracking-[0.3em] uppercase text-[var(--elt-gold)]">
          <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--elt-gold)] opacity-60" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--elt-gold)]" /></span>
          {t("hero.available")}
        </motion.div>
      </motion.div>
    </section>
  );
};
